import React, { useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import {
  currentMonthKey,
  homeSpendLastThreeMonths,
  homeSpendPeriodChipLabel,
  type HomeSpendPeriod,
} from '../../hooks/useHomeDashboard';

type Props = {
  value: HomeSpendPeriod;
  onChange: (next: HomeSpendPeriod) => void;
};

/** Recent-months toggle for the Home spend snapshot (this month + priors). */
export function SpendSnapshotPeriodFilter({ value, onChange }: Props) {
  const options = useMemo(() => {
    const thisMonth = currentMonthKey();
    const ids = homeSpendLastThreeMonths(thisMonth);
    // Keep a selected fallback month visible even if it’s older than the default window.
    if (value && !ids.includes(value)) {
      ids.push(value);
      ids.sort((a, b) => b.localeCompare(a));
    }
    return ids.map((id) => ({
      id,
      label: homeSpendPeriodChipLabel(id),
      accessibilityLabel:
        id === thisMonth ? 'This month' : homeSpendPeriodChipLabel(id),
    }));
  }, [value]);

  return (
    <View style={styles.wrap} accessibilityRole="tablist">
      {options.map((option) => {
        const selected = value === option.id;
        return (
          <Pressable
            key={option.id}
            accessibilityRole="tab"
            accessibilityState={{ selected }}
            accessibilityLabel={option.accessibilityLabel}
            onPress={() => onChange(option.id)}
            style={({ pressed }) => [
              styles.chip,
              selected && styles.chipSelected,
              pressed && styles.pressed,
            ]}
          >
            <Text style={[styles.chipText, selected && styles.chipTextSelected]}>
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    padding: 3,
    borderRadius: radii.pill,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.04)',
    alignSelf: 'flex-start',
  },
  chip: {
    minHeight: 32,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: radii.pill,
    justifyContent: 'center',
  },
  chipSelected: {
    backgroundColor: 'rgba(61, 214, 164, 0.18)',
  },
  chipText: {
    ...typography.caption,
    color: colors.textMuted,
    fontWeight: '600',
  },
  chipTextSelected: {
    color: colors.accent,
  },
  pressed: {
    opacity: 0.85,
  },
});
